import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useAppToast } from 'components/feedback/useAppToast'
import axiosInstance from 'services/axiosInstance'

interface PostParams<TVariables = unknown> {
  url: string
  payload?: TVariables
}

type UsePostOptions<TData, TVariables, TError = Error> = Omit<
  UseMutationOptions<TData, TError, PostParams<TVariables>>,
  'mutationFn'
> & {
  toast?: { success?: string; error?: string }
}

const usePost = <TData = unknown, TVariables = unknown, TError = Error>(
  options?: UsePostOptions<TData, TVariables, TError>,
) => {
  const { showToast } = useAppToast()
  const { toast, ...mutationOptions } = options ?? {}

  const postData = async ({ url, payload }: PostParams<TVariables>): Promise<TData> => {
    const { data } = await axiosInstance.post<TData>(url, payload)

    return data
  }

  return useMutation({
    mutationFn: postData,
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      if (toast !== undefined) {
        const apiMessage = (data as { message?: string })?.message
        const msg = apiMessage ?? toast?.success
        if (msg) showToast(msg, 'success')
      }

      mutationOptions?.onSuccess?.(data, variables, context, undefined as never)
    },
    onError: (error, variables, context) => {
      if (toast !== undefined) {
        const msg =
          toast?.error ?? (error as AxiosError<{ message?: string }>).response?.data?.message ?? 'Something went wrong.'

        showToast(msg, 'error')
      }

      mutationOptions?.onError?.(error, variables, context, undefined as never)
    },
  })
}

export default usePost
